import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Typography, Avatar, Chip, Stack, Paper, IconButton, Button,
  Divider, TextField, Select, MenuItem, FormControl, InputLabel,
  Tooltip, Snackbar, Alert, Card, CardContent, Collapse, Tabs, Tab,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import EventIcon from "@mui/icons-material/Event";
import PeopleIcon from "@mui/icons-material/People";
import MoneyIcon from "@mui/icons-material/AttachMoney";
import StarIcon from "@mui/icons-material/Star";
import FlagIcon from "@mui/icons-material/Flag";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WarningIcon from "@mui/icons-material/Warning";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ArrowDropDown as DropdownIcon } from "@mui/icons-material";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CheckboxMui from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Loader } from "../../Components/Loader";
import PremiumDateTimePicker from "../../Components/PremiumDateTimePicker";
import { formatDateTime, formatShortDate } from "../../utils/dateFormat";
import {
  fetchLeadThread, updateLead, addLeadNote, deleteLeadNote, fetchTeamUsers,
  sendEmailFromLead,
  fetchRelatedLeads, mergeLead, archiveLead, restoreLead,
  markLeadAsPrimary, flagLeadSameEvent, trashLead,
  getLeadAccessMode, canActOnLead,
  type LeadThread, type LeadNote, type LeadActivity, type TeamUser, type LeadEmail,
  STATUS_CONFIG, PRIORITY_CONFIG,
} from "./leadApi";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Menu, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { usePermission } from "../../hooks/usePermission";
import { useLeadBus } from "../../hooks/useLeadBus";
import TaskDetailDialog from "../Tasks/TaskDetailDialog";
import LeadNumberBadge from "./LeadNumberBadge";
import CustomerRelationshipPanel from "./CustomerRelationshipPanel";
import MergeLeadDialog from "./MergeLeadDialog";
import FlagSameEventDialog from "./FlagSameEventDialog";
import LeadActionsPanel from "./ContactLogPanel";
import LeadSmsHistoryCard from "./LeadSmsHistoryCard";
import AssignmentHistoryPanel from "./AssignmentHistoryPanel";
import LeadActivityTimeline from "./LeadActivityTimeline";
import EditLeadDialog from "./EditLeadDialog";
import { formatEventDate, formatLeadBudget } from "./leadCellUtils";
import {
  fetchLeadTasks, fetchLeadContacts, updateTask, logContact, createTask,
  type Task, type LeadContact, PRIORITY_CONFIG as TASK_PRIORITY, CONTACT_TYPES, OUTCOME_OPTIONS,
} from "../Tasks/taskApi";
import { useContactActions } from "../../Components/ContactActions";

const NAVY = "#1a3353";
const GOLD = "#D4AF37";

/* ── Premium surface tokens — applied to every card on the page so
   the visual feels coherent and luxurious. Soft borders and a layered
   shadow give an enterprise card surface depth without hard outlines.
   The page bg carries a hint of warmth (#FAF8F4) so white cards have
   a quiet visual frame around them — matches the reference design. */
const SURFACE = {
  bg:         "#faf8f4",                                   // page background (warm off-white)
  card:       "#ffffff",                                   // card body
  cardBorder: "1px solid #eef0f4",                         // hair-thin border
  cardShadow: "0 1px 2px rgba(13,27,42,0.04), 0 4px 16px rgba(13,27,42,0.04)",
  cardRadius: "14px",
  cardHover:  "0 6px 24px rgba(13,27,42,0.06)",
  divider:    "1px solid #f1f3f6",
} as const;

const STATUSES = Object.entries(STATUS_CONFIG).map(([k, v]) => ({ value: k, ...v }));

const InfoRow: React.FC<{ icon: React.ReactNode; label: string; value: string | null | undefined }> = ({ icon, label, value }) => (
  <Stack direction="row" alignItems="center" gap={1.5} py={0.8}>
    <Box sx={{ color: "#94a3b8", minWidth: 20, display: "flex" }}>{icon}</Box>
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>{label}</Typography>
    <Typography variant="body2" fontWeight={500} sx={{ textTransform: label === "Name" ? "capitalize" : "none" }}>
      {value || "—"}
    </Typography>
  </Stack>
);

/* InfoCell — horizontal pill used by the Customer Info Strip across the top
   of the lead detail page. Compact icon + label/value + optional subtitle,
   clickable when the cell represents a navigable entity (e.g. customer). */
const InfoCell: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | null | undefined;
  subtitle?: string;
  capitalize?: boolean;
  mono?: boolean;
  onClick?: () => void;
}> = ({ icon, label, value, subtitle, capitalize, mono, onClick }) => (
  <Stack
    direction="row" alignItems="center" gap={1}
    onClick={onClick}
    sx={{
      minWidth: 0,
      cursor: onClick ? "pointer" : "default",
      "&:hover": onClick ? { "& .__value": { color: "#D4AF37" } } : {},
    }}
  >
    <Box sx={{
      width: 30, height: 30, borderRadius: 1.2, flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      bgcolor: "#f1f5f9", color: "#475569",
    }}>
      {icon}
    </Box>
    <Box minWidth={0}>
      <Typography sx={{
        fontSize: 9.5, fontWeight: 700, color: "#94a3b8",
        letterSpacing: 0.6, textTransform: "uppercase", lineHeight: 1,
      }}>
        {label}
      </Typography>
      <Typography
        className="__value"
        sx={{
          fontSize: 13, fontWeight: 700, color: "#1E4D8F",
          lineHeight: 1.25, mt: 0.3,
          maxWidth: 180,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          textTransform: capitalize ? "capitalize" : "none",
          fontFamily: mono ? "'SF Mono','JetBrains Mono','Roboto Mono',Menlo,Consolas,monospace" : undefined,
          transition: "color 0.15s",
          textDecoration: onClick ? "none" : "none",
        }}
        title={value ?? undefined}
      >
        {value || "—"}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontSize: 10.5, color: "#94a3b8", lineHeight: 1, mt: 0.25 }} noWrap>
          {subtitle}
        </Typography>
      )}
    </Box>
  </Stack>
);

/* ── Timeline helpers ────────────────────────────────────────────────────
 * Backend stores the activity's change-payload in `changes` (JSON) and only
 * fills `description` for a few types (created / note_added / inquiry).
 * These helpers derive a readable title + per-field diffs so EVERY activity
 * row renders something — never a blank line.
 * ──────────────────────────────────────────────────────────────────────── */

// Single-field labels for the "Priority changed", "Event date updated", etc.
// smarter titles when type === 'updated' touches exactly one tracked field.
const SINGLE_FIELD_UPDATE_TITLE: Record<string, string> = {
  priority:           "Priority changed",
  event_type:         "Event type updated",
  event_date:         "Event date updated",
  guest_count:        "Guest count updated",
  budget_value:       "Budget updated",
  budget_known:       "Budget updated",
  preferred_hall:     "Preferred hall updated",
  is_vip:             "VIP flag updated",
  follow_up_required: "Follow-up flag updated",
  follow_up_at:       "Follow-up time updated",
  name:               "Name updated",
  email:              "Email updated",
  phone:              "Phone updated",
  organization:       "Organization updated",
  customer_id:        "Linked client updated",
};

// Pretty-print a date/time string from activity meta for the task-event titles.
// Returns null when the input is missing/unparseable so the caller can decide
// whether to omit the subtitle entirely.
function formatMetaDateTime(input: unknown): string | null {
  if (!input || typeof input !== "string") return null;
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
}

// Title-case a contact outcome stored as snake_case in meta.outcome.
function prettyOutcome(o: unknown): string | null {
  if (!o || typeof o !== "string") return null;
  return o.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function activityTitle(a: LeadActivity): string {
  switch (a.type) {
    case "created":             return "Lead created";
    case "status_changed":      return "Status changed";
    case "assigned":            return "Lead assigned";
    case "note_added":          return "Note added";
    case "email_linked":        return "Email linked";
    case "email_unlinked":      return "Email unlinked";
    case "email_sent":          return "Email sent";
    // Historical rows (pre-redesign) used "duplicate_prevented" with the
    // old "Inquiry merged — already tracked as lead" wording. Reframe to
    // match the new always-create flow: every enquiry creates a lead, the
    // event simply records that the customer was a repeat enquirer.
    case "duplicate_prevented": return "Related enquiry detected";
    case "inquiry_received":    return "Inquiry received";

    // Same-event grouping lifecycle (operator-driven, never INSERT-time).
    case "lead_primary_event":      return "★ Marked as primary event";
    case "lead_flagged_same_event": {
      const pid = a.meta?.primary_id;
      return pid ? `Flagged as same event as #${pid}` : "Flagged as same event";
    }
    case "lead_same_event_linked": {
      const cid = a.meta?.child_id;
      return cid ? `Same-event lead #${cid} linked` : "Same-event lead linked";
    }

    // Contact + task lifecycle. Titles read meta from the backend event so
    // the user gets specific text ("Call • Interested", "Follow-up scheduled
    // · May 16, 3:00 PM") rather than the generic "Lead updated" fallback.
    case "contact_logged": {
      const kind    = typeof a.meta?.type === "string"
        ? a.meta.type[0].toUpperCase() + a.meta.type.slice(1)
        : "Contact";
      const outcome = prettyOutcome(a.meta?.outcome);
      return outcome ? `${kind} • ${outcome}` : `${kind} logged`;
    }
    case "task_created": {
      const when = formatMetaDateTime(a.meta?.due_at);
      return when ? `Follow-up scheduled · ${when}` : "Follow-up scheduled";
    }
    case "task_completed":   return "Follow-up completed";
    case "task_cancelled":   return "Follow-up cancelled";
    case "task_reassigned":  return "Follow-up reassigned";
    case "task_rescheduled": {
      const when = formatMetaDateTime(a.meta?.due_at_to ?? a.meta?.due_at);
      return when ? `Follow-up rescheduled · ${when}` : "Follow-up rescheduled";
    }
    case "task_updated":     return "Task updated";

    // Resolution lifecycle (admin merge / archive / restore).
    case "lead_archived":         return "Lead archived";
    case "lead_marked_duplicate": return "Marked as duplicate";
    case "lead_restored":         return "Lead restored to active";
    case "lead_merged": {
      const into = a.meta?.target_id;
      return into ? `Merged into #${into}` : "Merged into another lead";
    }
    case "lead_merged_in": {
      const from = a.meta?.source_id;
      return from ? `Absorbed lead #${from}` : "Absorbed another lead";
    }
  }
  // type === 'updated' (or anything unknown): try to derive a smarter title
  // from the changed-field set; ai_score moves automatically alongside real
  // edits, so it doesn't count as the "real" subject of the update.
  if (a.changes) {
    const keys = Object.keys(a.changes).filter((k) => k !== "ai_score");
    if (keys.length === 1) {
      return SINGLE_FIELD_UPDATE_TITLE[keys[0]] ?? "Lead updated";
    }
  }
  return "Lead updated"; // generic fallback — never blank
}

function activityColor(type: string): string {
  if (type === "created")          return "#3b82f6";
  if (type === "status_changed")   return "#8b5cf6";
  if (type === "assigned")         return "#14b8a6";
  if (type === "note_added")       return "#f59e0b";
  if (type === "updated")          return "#6366f1";
  if (type === "contact_logged")   return "#0ea5e9";
  if (type === "task_completed")   return "#22c55e";
  if (type === "task_cancelled")   return "#94a3b8";
  if (type.startsWith("task_"))    return "#f59e0b"; // task_created / rescheduled / reassigned / updated
  if (type.includes("email"))      return "#0ea5e9";
  // Resolution lifecycle palette
  if (type === "lead_merged"
   || type === "lead_merged_in")          return "#7c3aed"; // purple — merge ties
  if (type === "lead_archived"
   || type === "lead_marked_duplicate")   return "#64748b"; // slate — withdrawn
  if (type === "lead_restored")           return "#22c55e"; // green — reinstated
  // Same-event grouping
  if (type === "lead_primary_event")      return "#b45309"; // amber — primary star
  if (type === "lead_flagged_same_event"
   || type === "lead_same_event_linked")  return "#6d28d9"; // violet — same-event link
  if (type === "duplicate_prevented")     return "#0ea5e9"; // cyan — informational
  return "#94a3b8";
}

const FIELD_DISPLAY_LABEL: Record<string, string> = {
  status:             "Status",
  assigned_to:        "Assigned to",
  priority:           "Priority",
  ai_score:           "AI score",
  event_type:         "Event type",
  event_date:         "Event date",
  guest_count:        "Guests",
  budget_value:       "Budget",
  budget_known:       "Budget known",
  preferred_hall:     "Preferred hall",
  is_vip:             "VIP",
  follow_up_required: "Follow-up required",
  follow_up_at:       "Follow-up at",
  customer_id:        "Linked client",
  name:               "Name",
  email:              "Email",
  phone:              "Phone",
  organization:       "Organization",
};

function fieldLabel(field: string): string {
  return (
    FIELD_DISPLAY_LABEL[field] ??
    field.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

// "Due in 25 min" / "12h overdue" — amber under 1h, red when past due.
// Recomputed every render (60s tick drives re-render).
function dueInLabel(iso: string | null | undefined): { text: string; color: string } | null {
  if (!iso) return null;
  const diffMs = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(diffMs)) return null;
  const absMin = Math.round(Math.abs(diffMs) / 60_000);
  const past   = diffMs < 0;
  let text: string;
  if (absMin < 1)            text = past ? "just past due" : "due now";
  else if (absMin < 60)      text = `${past ? "" : "in "}${absMin} min${past ? " overdue" : ""}`;
  else if (absMin < 60 * 24) text = `${past ? "" : "in "}${Math.round(absMin / 60)}h${past ? " overdue" : ""}`;
  else                        text = `${past ? "" : "in "}${Math.round(absMin / (60 * 24))}d${past ? " overdue" : ""}`;
  const color = past ? "#dc2626" : (absMin <= 60 ? "#b45309" : "#64748b");
  return { text, color };
}

function diffSide(side: { value: any; label: string | null } | undefined): string {
  if (!side) return "—";
  if (side.label !== null && side.label !== undefined && side.label !== "") return side.label;
  if (side.value !== null && side.value !== undefined && side.value !== "") return String(side.value);
  return "—";
}

export default function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [lead, setLead] = useState<LeadThread | null>(null);
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [contacts, setContacts] = useState<LeadContact[]>([]);
  const [teamUsers, setTeamUsers] = useState<TeamUser[]>([]);
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" as "success" | "error" });
  // Inline log contact form
  const [contactType, setContactType] = useState<string>("call");
  const [contactOutcome, setContactOutcome] = useState<string>("");
  const [contactNotes, setContactNotes] = useState("");
  const [followUp, setFollowUp] = useState(false);
  const [followUpDate, setFollowUpDate] = useState("");
  const [followUpNotes, setFollowUpNotes] = useState("");
  // Email compose
  const [showCompose, setShowCompose] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailAttachments, setEmailAttachments] = useState<File[]>([]);
  const [sendingEmail, setSendingEmail] = useState(false);

  /* ── Header quick-action wiring ───────────────────────────────────
   * The hero's right-edge command rail (Call · Email · Note · Event)
   * routes through three surfaces:
   *
   *   • Call  → useContactActions().handleCallAction — tel: URI on a
   *             sanitized phone, snackbar when blank. Same handler as
   *             every other CRM surface.
   *   • Email → useContactActions().handleEmailAction — opens the
   *             canonical EmailHub ComposeModal pre-filled with the
   *             lead's email + threadId. NEVER mailto:. onSent fires
   *             loadAll() so the Timeline picks up the sent record.
   *   • Note  → headerNoteOpen — small Dialog with an autofocused
   *             textarea + Save button. Save goes through addLeadNote
   *             (same endpoint the inline panel uses) and refreshes
   *             the timeline on success.
   *   • Event → headerScheduleOpen — Dialog wrapping
   *             PremiumDateTimePicker + priority + notes. Save calls
   *             createTask (same path as QuickActionsCard's "Schedule
   *             Follow-up" tile) and refreshes the timeline.
   *
   * The Call / Email loading + disabled rules are owned by the hook;
   * Note / Schedule are simple dialogs because there's no shared
   * "open me from anywhere" requirement for those two yet. */
  const { handleCallAction, handleEmailAction, emailLoading, modalElement: emailModalElement } =
    useContactActions();
  const [headerNoteOpen,     setHeaderNoteOpen]     = useState(false);
  const [headerNoteText,     setHeaderNoteText]     = useState("");
  const [headerNoteSaving,   setHeaderNoteSaving]   = useState(false);
  const [headerScheduleOpen, setHeaderScheduleOpen] = useState(false);
  const [headerSchedDate,    setHeaderSchedDate]    = useState("");
  const [headerSchedPriority,setHeaderSchedPriority]= useState<"low"|"medium"|"high"|"urgent">("medium");
  const [headerSchedNotes,   setHeaderSchedNotes]   = useState("");
  const [headerSchedSaving,  setHeaderSchedSaving]  = useState(false);
  // Email body expand
  const [expandedEmails, setExpandedEmails] = useState<Set<number>>(new Set());

  // Resolution lifecycle UI state — admin menu + dialogs + related leads.
  const { isAdmin, userId, canEdit } = usePermission();

  /* ── Lead-ownership access mode ──────────────────────────────────────
   * Mirrors backend LeadThread::userCanAct() exactly:
   *   admin    — Admin / Super Admin
   *   owner    — currently-assigned user
   *   previous — appears in lead.previous_assignee_ids → READ-ONLY
   *   none     — no relationship (shouldn't normally see the lead, but
   *              treat as read-only if they do)
   * `canAct` is the single boolean every action gate uses below; when
   * false we surface the warning banner and disable every action button.
   * ─────────────────────────────────────────────────────────────────── */
  const accessMode = getLeadAccessMode(lead, isAdmin, userId);
  const canAct     = canActOnLead(accessMode);
  const isReadOnlyHistorical = accessMode === "previous" || (accessMode === "none" && !!lead);

  // Resolution lifecycle is admin-or-leads-edit, BUT previous assignees
  // who happen to have leads.edit must still be locked out — they no
  // longer "own" this particular lead's lifecycle.
  const canManageResolution = (isAdmin || canEdit("leads")) && canAct;

  // Active CRM stat card — drives the inline detail panel under the row.
  // null = nothing selected (panel collapsed). One of:
  //   total · active · archived · same_event · quotes · bookings
  const [activeStatCard, setActiveStatCard] = useState<string | null>(null);

  // Active tab in the unified Communications panel.
  //   notes   → internal team notes (the messenger composer's audience)
  //   emails  → outbound + inbound emails on this lead
  //   inquiry → the original inquiry / source message
  // How many entries of the full activity stream to show. Date-grouped
  // with a "Show more" affordance so the page can't grow unbounded.
  const TIMELINE_PAGE = 12;
  const [timelineLimit, setTimelineLimit] = useState<number>(TIMELINE_PAGE);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<HTMLElement | null>(null);
  const [archiveDialog, setArchiveDialog]       = useState<null | { resolution: "archived" | "duplicate" | "cancelled" | "lost"; title: string }>(null);
  const [archiveReason, setArchiveReason]       = useState("");
  const [mergeDialogOpen, setMergeDialogOpen]   = useState(false);
  const [relatedLeads, setRelatedLeads]         = useState<LeadThread[]>([]);
  const [editOpen, setEditOpen]                 = useState(false);
  // Bumps on every loadAll() so child cards keyed off it (SMS history,
  // etc.) refetch after any mutation — SMS sends don't change lead.version
  // but they DO trigger loadAll() via QuickActionsCard's onChanged hook.
  const [refreshCounter, setRefreshCounter]     = useState(0);

  const loadAll = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [leadData, tasksData, contactsData, users, rel] = await Promise.all([
        fetchLeadThread(Number(id)),
        fetchLeadTasks(Number(id)),
        fetchLeadContacts(Number(id)),
        fetchTeamUsers(),
        fetchRelatedLeads(Number(id)).catch(() => [] as LeadThread[]),
      ]);
      setLead(leadData);
      setTasks(tasksData);
      setContacts(contactsData);
      setTeamUsers(users);
      // Defensive: the /related endpoint already excludes the current
      // lead, but filter again here so the panel + merge dialog + same-
      // event picker can never accidentally self-reference if a stale
      // cache or future regression leaks it through.
      setRelatedLeads(rel.filter((r) => r.id !== Number(id)));
      setRefreshCounter((c) => c + 1);
    } catch {}
    setLoading(false);
  }, [id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  /* ── External-mutation sync ──────────────────────────────────
     If someone else (or this same user in another tab) updates
     THIS lead — status drop on the pipeline kanban, admin
     reassignment from the leads table, same-event flag from a
     related lead's drawer, etc. — refetch so the header /
     timeline / panels reflect the new state.
     Events for OTHER leads are ignored to avoid wasted work.
     Catch-all signals (refresh-all, note delete with leadId=0)
     also trigger a refetch since they could affect any lead. */
  useLeadBus((ev) => {
    if (!lead) return;
    if (ev.leadId === lead.id || ev.leadId === 0) {
      loadAll();
    }
    // Related-lead view also depends on customer-linked changes;
    // for "same_event" or "primary" events we always refetch so
    // the Related Enquiries panel stays consistent.
    else if (ev.type === "lead.same_event" || ev.type === "lead.primary") {
      loadAll();
    }
  });

  const handleUpdate = async (field: string, value: any) => {
    if (!lead) return;
    setSaving(true);
    try {
      await updateLead(lead.id, { [field]: value });
      setLead((prev) => prev ? { ...prev, [field]: value } : prev);
      setSnack({ open: true, msg: "Updated", severity: "success" });
    } catch {}
    setSaving(false);
  };

  const handleAddNote = async () => {
    if (!lead || !noteText.trim()) return;
    setSaving(true);
    try {
      await addLeadNote(lead.id, noteText);
      setNoteText("");
      loadAll();
      setSnack({ open: true, msg: "Note added", severity: "success" });
    } catch {}
    setSaving(false);
  };

  const handleLogContact = async () => {
    if (!lead) return;
    // Defensive: backend validator catches this, but the button is also
    // disabled in the UI — keeps the two checks aligned.
    if (followUp && !followUpDate) return;
    setSaving(true);
    try {
      const res = await logContact({
        lead_id: lead.id,
        type: contactType,
        outcome: contactOutcome || undefined,
        notes: contactNotes || undefined,
        follow_up: followUp,
        follow_up_date: followUp ? followUpDate : undefined,
        follow_up_notes: followUp ? followUpNotes : undefined,
      });
      setContactType("call"); setContactOutcome(""); setContactNotes("");
      setFollowUp(false); setFollowUpDate(""); setFollowUpNotes("");
      loadAll();
      // Specific success message when a task was actually scheduled —
      // confirms the reminder is real and shows the exact due time.
      let msg = "Contact logged";
      if (res?.task?.due_at) {
        const when = new Date(res.task.due_at).toLocaleString("en-US", {
          month: "short", day: "numeric", year: "numeric",
          hour: "numeric", minute: "2-digit", hour12: true,
        });
        msg = `Contact logged • Follow-up scheduled for ${when}`;
      }
      setSnack({ open: true, msg, severity: "success" });
    } catch (e: any) {
      // Surface the real failure to the operator. Silently swallowing here
      // produced the original bug report ("note disappeared") — the
      // backend was actually returning 422 / 500 and the rep saw a fully
      // reset form with no acknowledgement either way. We also keep the
      // typed contact notes on the form so the rep doesn't lose context
      // when fixing whatever the validator flagged.
      // eslint-disable-next-line no-console
      console.error("logContact failed", e);
      const reason =
        (typeof e?.message === "string" && e.message) ||
        "Couldn't log contact. Please try again.";
      setSnack({ open: true, msg: reason, severity: "error" });
    }
    setSaving(false);
  };

  const handleDeleteNote = async (noteId: number) => {
    try {
      await deleteLeadNote(noteId);
      loadAll();
    } catch {}
  };

  const handleSendEmail = async () => {
    if (!lead || !emailSubject.trim() || !emailBody.trim()) return;
    setSendingEmail(true);
    try {
      let payload: FormData | { subject: string; body: string };
      if (emailAttachments.length > 0) {
        const fd = new FormData();
        fd.append("subject", emailSubject);
        fd.append("body", emailBody);
        emailAttachments.forEach((f) => fd.append("attachments[]", f));
        payload = fd;
      } else {
        payload = { subject: emailSubject, body: emailBody };
      }
      await sendEmailFromLead(lead.id, payload);
      setEmailSubject("");
      setEmailBody("");
      setEmailAttachments([]);
      setShowCompose(false);
      loadAll();
      setSnack({ open: true, msg: "Email sent successfully", severity: "success" });
    } catch {
      setSnack({ open: true, msg: "Failed to send email", severity: "error" });
    }
    setSendingEmail(false);
  };

  /* Save handler for the header Add Note dialog. Routes through the
     same addLeadNote endpoint as the inline composer below the page,
     so the timeline + activity stream see a single, consistent shape
     regardless of which surface the rep used to add the note. */
  const handleHeaderNoteSave = useCallback(async () => {
    if (!lead || !headerNoteText.trim()) return;
    setHeaderNoteSaving(true);
    try {
      await addLeadNote(lead.id, headerNoteText.trim());
      setHeaderNoteText("");
      setHeaderNoteOpen(false);
      await loadAll();
      setSnack({ open: true, msg: "Note added", severity: "success" });
    } catch (e: any) {
      setSnack({ open: true, msg: e?.message || "Couldn't save note", severity: "error" });
    }
    setHeaderNoteSaving(false);
  }, [lead, headerNoteText, loadAll]);

  /* Save handler for the header Schedule dialog. Mirrors the
     "Schedule Follow-up" tile inside QuickActionsCard so the
     resulting Task has identical shape (lead_id, due_at, priority,
     notes, assignee defaults to current owner). */
  const handleHeaderScheduleSave = useCallback(async () => {
    if (!lead || !headerSchedDate) return;
    setHeaderSchedSaving(true);
    try {
      await createTask({
        title: `Follow up with ${lead.name || "lead"}`,
        lead_id: lead.id,
        assigned_to: lead.assigned_to ?? undefined,
        due_at: headerSchedDate,
        priority: headerSchedPriority,
        notes: headerSchedNotes || undefined,
      });
      setHeaderSchedDate("");
      setHeaderSchedPriority("medium");
      setHeaderSchedNotes("");
      setHeaderScheduleOpen(false);
      await loadAll();
      setSnack({ open: true, msg: "Follow-up scheduled", severity: "success" });
    } catch (e: any) {
      setSnack({ open: true, msg: e?.message || "Couldn't schedule follow-up", severity: "error" });
    }
    setHeaderSchedSaving(false);
  }, [lead, headerSchedDate, headerSchedPriority, headerSchedNotes, loadAll]);

  const toggleEmailExpand = (emailId: number) => {
    setExpandedEmails((prev) => {
      const next = new Set(prev);
      next.has(emailId) ? next.delete(emailId) : next.add(emailId);
      return next;
    });
  };

  const handleCompleteTask = async (taskId: number) => {
    try {
      await updateTask(taskId, { status: "completed" });
      // loadAll() refreshes the lead — LeadTaskObserver has already updated
      // follow_up_required/at, so the chip everywhere stays in sync.
      loadAll();
      setSnack({ open: true, msg: "Follow-up completed", severity: "success" });
    } catch {}
  };

  // Inline reschedule — date is held in `rescheduleDraft` so the picker
  // can be opened/cancelled without an extra modal.
  const [reschedulingTaskId, setReschedulingTaskId] = useState<number | null>(null);
  const [rescheduleDraft, setRescheduleDraft]       = useState<string>("");

  // Task detail dialog — opens the same TaskDetailDialog used on the My
  // Tasks page so reassign / in-progress / edit-notes / edit-priority are
  // reachable here too without leaving the lead.
  const [detailTask, setDetailTask] = useState<Task | null>(null);

  // 60s tick so dueInLabel + isOverdue re-render without a page reload.
  const [, setMinuteTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setMinuteTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const openReschedule = (taskId: number, currentDue: string) => {
    setReschedulingTaskId(taskId);
    setRescheduleDraft(currentDue);
  };
  const cancelReschedule = () => {
    setReschedulingTaskId(null);
    setRescheduleDraft("");
  };
  const submitReschedule = async () => {
    if (!reschedulingTaskId || !rescheduleDraft) return;
    try {
      await updateTask(reschedulingTaskId, { due_at: rescheduleDraft });
      cancelReschedule();
      loadAll();
      setSnack({ open: true, msg: "Follow-up rescheduled", severity: "success" });
    } catch {}
  };

  const handleCancelTask = async (taskId: number) => {
    try {
      // Soft cancel — keeps the row + audit history, fires the
      // LeadTaskObserver so dashboard counters recompute, and emits a
      // task_cancelled timeline event via TaskController::update. Hard
      // delete is admin-only now (Fix #2/#3).
      await updateTask(taskId, { status: "cancelled" });
      loadAll();
      setSnack({ open: true, msg: "Follow-up cancelled", severity: "success" });
    } catch (e: any) {
      setSnack({ open: true, msg: e?.message ?? "Cancel failed", severity: "error" });
    }
  };

  /* ── Resolution lifecycle handlers ──────────────────────────────────── */

  const openArchiveDialog = (resolution: "archived" | "duplicate" | "cancelled" | "lost", title: string) => {
    setActionMenuAnchor(null);
    setArchiveReason("");
    setArchiveDialog({ resolution, title });
  };

  const confirmArchive = async () => {
    if (!lead || !archiveDialog) return;
    try {
      await archiveLead(lead.id, { reason: archiveReason || undefined, resolution: archiveDialog.resolution });
      setArchiveDialog(null);
      loadAll();
      setSnack({ open: true, msg: `Lead ${archiveDialog.resolution === "duplicate" ? "marked as duplicate" : "archived"}`, severity: "success" });
    } catch (e: any) {
      setSnack({ open: true, msg: e?.message ?? "Archive failed", severity: "error" });
    }
  };

  const handleRestore = async () => {
    if (!lead) return;
    setActionMenuAnchor(null);
    try {
      await restoreLead(lead.id);
      loadAll();
      setSnack({ open: true, msg: "Lead restored to active", severity: "success" });
    } catch (e: any) {
      setSnack({ open: true, msg: e?.message ?? "Restore failed", severity: "error" });
    }
  };

  const openMergeDialog = () => {
    setActionMenuAnchor(null);
    setMergeDialogOpen(true);
  };

  /* ── Same-event grouping handlers ───────────────────────────────────── */

  const handleMarkPrimary = async () => {
    if (!lead) return;
    setActionMenuAnchor(null);
    try {
      await markLeadAsPrimary(lead.id);
      loadAll();
      setSnack({ open: true, msg: "Marked as primary event", severity: "success" });
    } catch (e: any) {
      setSnack({ open: true, msg: e?.message ?? "Mark primary failed", severity: "error" });
    }
  };

  const [sameEventDialogOpen, setSameEventDialogOpen] = useState(false);
  const openSameEventDialog = () => {
    setActionMenuAnchor(null);
    setSameEventDialogOpen(true);
  };
  // FlagSameEventDialog owns its own primary-picker + reason-chip state;
  // the parent only needs to fire the API call when the operator confirms.
  // Throws are re-thrown so the dialog's submitting state clears.
  const handleConfirmSameEvent = async (primaryId: number, compiledReason: string) => {
    if (!lead) return;
    if (!Number.isInteger(primaryId) || primaryId <= 0 || primaryId === lead.id) {
      setSnack({ open: true, msg: "Pick a valid primary lead", severity: "error" });
      return;
    }
    try {
      await flagLeadSameEvent(lead.id, { primary_id: primaryId, reason: compiledReason || undefined });
      setSameEventDialogOpen(false);
      loadAll();
      setSnack({ open: true, msg: `Flagged as same event as #${primaryId}`, severity: "success" });
    } catch (e: any) {
      setSnack({ open: true, msg: e?.message ?? "Flag same-event failed", severity: "error" });
      throw e;
    }
  };

  const handleMoveToTrash = async () => {
    if (!lead) return;
    setActionMenuAnchor(null);
    try {
      await trashLead(lead.id);
      loadAll();
      setSnack({ open: true, msg: "Lead moved to trash", severity: "success" });
    } catch (e: any) {
      setSnack({ open: true, msg: e?.message ?? "Trash failed", severity: "error" });
    }
  };

  // Called by MergeLeadDialog after the operator confirms the target. The
  // dialog already validated the target id + ran its own confirmation
  // summary, so this handler just executes the merge and surfaces the
  // outcome. Throws are re-thrown so the dialog's submitting state clears.
  const handleConfirmMerge = async (targetId: number) => {
    if (!lead) return;
    if (!Number.isInteger(targetId) || targetId <= 0 || targetId === lead.id) {
      setSnack({ open: true, msg: "Pick a valid target lead", severity: "error" });
      return;
    }
    try {
      await mergeLead(lead.id, targetId);
      setMergeDialogOpen(false);
      // After merge, the source IS archived — keep the user on the page so
      // they can see the "Merged into #X" banner and follow the link.
      loadAll();
      setSnack({ open: true, msg: `Merged into lead #${targetId}`, severity: "success" });
    } catch (e: any) {
      setSnack({ open: true, msg: e?.message ?? "Merge failed", severity: "error" });
      throw e;
    }
  };

  if (loading) return <Loader height="80vh" />;
  if (!lead) return <Box p={4}><Typography>Lead not found</Typography></Box>;

  const statusCfg = STATUS_CONFIG[lead.status] || {};
  const initials = lead.name?.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2);
  const pendingTasks = tasks.filter((t) => t.status === "pending" || t.status === "in_progress");
  const completedTasks = tasks.filter((t) => t.status === "completed");

  // Flatten all emails from linked threads, sorted oldest → newest
  const allEmails: LeadEmail[] = (lead.email_threads || [])
    .flatMap((et: any) => et.emails || [])
    .sort((a: LeadEmail, b: LeadEmail) => new Date(a.received_at).getTime() - new Date(b.received_at).getTime());

  // Merge activities + contacts for the lead-page main timeline.
  // CRM-history filtering rule (operational-thread extension):
  //   Hide "minor attempt detail" — anything that should only appear inside
  //   the follow-up modal's history view:
  //     • contact_logged rows whose meta.task_id is set (= attempt-on-thread)
  //     • lead_contacts rows whose task_id is set (= same, from contacts list)
  //     • task_updated rows whose meta.via='first_attempt' (auto pending→
  //       in_progress transition emitted by LeadContactController)
  //   Kept visible: task_created / task_completed / task_cancelled /
  //   task_rescheduled / task_reassigned / lead_* lifecycle rows. These
  //   are the "summarised follow-up events" the spec lists.
  const timeline = [
    ...(lead.activities || [])
      .filter((a: LeadActivity) => {
        if (a.type === "contact_logged" && a.meta?.task_id) return false;
        if (a.type === "task_updated" && a.meta?.via === "first_attempt") return false;
        return true;
      })
      .map((a: LeadActivity) => ({
        type: "activity" as const, date: a.created_at, data: a,
      })),
    ...contacts
      .filter((c) => !(c as any).task_id)
      .map((c) => ({
        type: "contact" as const, date: c.contacted_at, data: c,
      })),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: SURFACE.bg }}>
      {/* ══════════════════════════════════════════════════════════════
          Read-only historical banner

          Surfaces at the very top of the page when the current user is a
          PREVIOUS assignee on this lead. They keep timeline / customer /
          inquiry visibility (per the spec) but every action button is
          disabled below and every mutation route returns 403 on the
          server. The amber palette differentiates this from the green
          "active workspace" feel so the rep can't miss that they no
          longer own the lead. Admin / current assignee never see it.
          ══════════════════════════════════════════════════════════════ */}
      {isReadOnlyHistorical && (
        <Box sx={{
          mx: { xs: 1.6, md: 2.4 },
          mt: { xs: 1.6, md: 2 },
          px: 2.2, py: 1.4,
          borderRadius: 1.6,
          bgcolor: "#FEF7E6",
          border: "1px solid #F5D58A",
          display: "flex", alignItems: "center", gap: 1.4,
          boxShadow: "0 1px 2px rgba(202,138,4,0.06)",
        }}>
          <WarningIcon sx={{ color: "#B45309", fontSize: 22, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" alignItems="center" gap={0.8} flexWrap="wrap">
              <Typography sx={{
                fontSize: 13.5, fontWeight: 700, color: "#92400E",
                lineHeight: 1.2,
              }}>
                Read Only – Previously Assigned
              </Typography>
              {/* Archive/History badge — quick visual cue that this row is
                  read-only and lives in the user's assignment history,
                  even when the banner text gets clipped on narrow screens. */}
              <Box sx={{
                px: 0.8, py: 0.15, borderRadius: 1,
                bgcolor: "#B45309", color: "#fff",
                fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4,
                textTransform: "uppercase",
              }}>
                History
              </Box>
            </Stack>
            <Typography sx={{
              fontSize: 11.5, fontWeight: 500, color: "#A16207",
              lineHeight: 1.3, mt: 0.4,
            }}>
              This lead has been reassigned. You can view the timeline, customer details,
              your own notes/calls, and the inquiry. Only the current assignee or an
              administrator can edit, add notes, log calls, change status, send mail, or
              reassign.
            </Typography>
          </Box>
        </Box>
      )}

      {/* ══════════════════════════════════════════════════════════════
          Premium CRM Hero — rounded margined card (not a full-width
          strip) with a layered luxury treatment:
            • base: deep-navy gradient corner→corner
            • bloom: soft gold radial highlight at the top right
            • texture: faint dot pattern on the right half so the card
              has subtle depth, not a flat slab
          The card sits inside the workspace padding so the page bg
          frames it like a hero on an enterprise CRM home.
          ══════════════════════════════════════════════════════════════ */}
      <Box sx={{
        pt: { xs: 1.6, md: 2 }, px: { xs: 1.6, md: 2.4 },
        flexShrink: 0, bgcolor: SURFACE.bg,
      }}>
        <Paper elevation={0} sx={{
          position: "relative", overflow: "hidden",
          borderRadius: "18px",
          maxWidth: 1700, mx: "auto",
          background: `
            radial-gradient(ellipse at 88% 0%, rgba(201,168,76,0.12) 0%, transparent 55%),
            linear-gradient(135deg, #1E4D8F 0%, #142b46 55%, #1c3a5f 100%)
          `,
          color: "#fff",
          px: { xs: 3, md: 4.5 }, py: { xs: 3, md: 3.6 },
          border: "1px solid rgba(201,168,76,0.18)",
          boxShadow: "0 1px 0 rgba(255,255,255,0.06) inset, 0 12px 36px rgba(13,27,42,0.22)",
          // Right-side dot pattern — more prominent than before, matches
          // the reference's textured navy treatment without overpowering.
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0, right: 0, bottom: 0,
            width: "50%",
            backgroundImage: "radial-gradient(rgba(255,255,255,0.08) 1.2px, transparent 1.2px)",
            backgroundSize: "14px 14px",
            maskImage: "linear-gradient(to left, rgba(0,0,0,0.85), transparent)",
            WebkitMaskImage: "linear-gradient(to left, rgba(0,0,0,0.85), transparent)",
            pointerEvents: "none",
          },
        }}>
        <Stack direction="row" alignItems="center" gap={2} sx={{ position: "relative", zIndex: 1 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.06)", "&:hover": { bgcolor: "rgba(255,255,255,0.14)" } }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>

          {/* Hero avatar — gold disc, the visual anchor of the header.
              72px to give the hero strong typographic weight on the left.
              Online presence dot sits at the bottom-right with a navy
              ring matching the hero background. */}
          <Box sx={{ position: "relative", flexShrink: 0, mr: { md: 0.6 } }}>
            <Avatar sx={{
              bgcolor: GOLD, color: NAVY, width: 72, height: 72,
              fontWeight: 800, fontSize: 26,
              boxShadow: `0 8px 22px ${GOLD}40`,
            }}>
              {initials}
            </Avatar>
            <Box sx={{
              position: "absolute", bottom: 2, right: 2,
              width: 14, height: 14, borderRadius: "50%",
              bgcolor: "#22c55e", border: "2.5px solid #142b46",
            }} />
          </Box>

          <Box flex={1} minWidth={0}>
            {/* "Inquiry Contact" tag + lead number on the upper line */}
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 0.1 }}>
              <Typography sx={{ fontSize: 9, fontWeight: 800, letterSpacing: 1, color: GOLD, textTransform: "uppercase", opacity: 0.85 }}>
                Inquiry Contact
              </Typography>
              {lead.lead_number && <LeadNumberBadge value={lead.lead_number} variant="header" />}
            </Stack>
            {/* The inquiry's submitted name — NEVER overwritten by customer account */}
            <Typography fontWeight={800} sx={{ textTransform: "capitalize", lineHeight: 1.15, fontSize: { xs: "1.35rem", md: "1.55rem" }, letterSpacing: "-0.4px" }}>
              {lead.name?.toLowerCase()}
            </Typography>
            <Stack direction="row" gap={0.7} alignItems="center" mt={0.6} flexWrap="wrap">
              <Chip size="small" label={statusCfg.label} sx={{ bgcolor: statusCfg.color, color: "#fff", fontWeight: 700, height: 22, fontSize: 10.5, letterSpacing: 0.3, borderRadius: "11px" }} />
              <Chip size="small" label={PRIORITY_CONFIG[lead.priority]?.label || lead.priority} sx={{ bgcolor: "rgba(255,255,255,0.10)", color: "#fff", border: "1px solid rgba(255,255,255,0.18)", height: 22, fontSize: 10.5, borderRadius: "11px" }} />
              {lead.is_vip && <Chip size="small" icon={<StarIcon sx={{ color: `${GOLD} !important`, fontSize: 12 }} />} label="VIP" sx={{ bgcolor: `${GOLD}22`, color: GOLD, height: 22, fontSize: 10.5, fontWeight: 700, border: `1px solid ${GOLD}55`, borderRadius: "11px" }} />}
              <Typography variant="caption" sx={{ opacity: 0.55, fontSize: 11, ml: 0.5 }}>
                {lead.source ? `via ${lead.source}` : "—"} · {new Date(lead.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </Typography>
            </Stack>

            {/* ── Linked Customer pill ──────────────────────────
                Separate, secondary identity. Only shown when a customer
                is actually linked. Displays the SHARED account name +
                phone + a count of how many enquiries are linked to it.
                Clicking opens the customer profile page — never replaces
                the inquiry identity above. */}
            {lead.customer_id && lead.customer && (() => {
              const cust = lead.customer!;
              const custName = cust.display_name?.trim() ?? "";
              const namesDiffer = custName.toLowerCase() !== (lead.name ?? "").trim().toLowerCase();
              const relatedCount = relatedLeads.length;
              return (
                <Box
                  onClick={() => navigate(`/customer-history/${lead.customer_id}`)}
                  sx={{
                    mt: 0.7, display: "inline-flex", alignItems: "center", gap: 0.8,
                    px: 1.1, py: 0.4, borderRadius: 1.2,
                    bgcolor: "rgba(201,168,76,0.12)",
                    border: `1px solid ${GOLD}40`,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    "&:hover": { bgcolor: "rgba(201,168,76,0.20)", borderColor: GOLD },
                  }}
                  title="Open linked customer profile"
                >
                  <Typography sx={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.8, color: GOLD, textTransform: "uppercase" }}>
                    Linked Customer
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#fff", textTransform: "capitalize" }}>
                    {custName.toLowerCase() || cust.phone || `#${cust.id}`}
                  </Typography>
                  {namesDiffer && (
                    <Box sx={{
                      fontSize: 8.5, fontWeight: 800, letterSpacing: 0.5,
                      bgcolor: GOLD, color: NAVY, borderRadius: 0.5,
                      px: 0.5, py: 0.05, textTransform: "uppercase",
                    }}>
                      Diff name
                    </Box>
                  )}
                  {cust.phone && (
                    <Typography sx={{ fontSize: 10.5, color: "rgba(255,255,255,0.7)" }}>
                      📞 {cust.phone}
                    </Typography>
                  )}
                  {relatedCount > 0 && (
                    <Typography sx={{ fontSize: 10.5, color: "rgba(255,255,255,0.7)", borderLeft: "1px solid rgba(255,255,255,0.2)", pl: 0.7 }}>
                      {relatedCount + 1} related {relatedCount === 0 ? "enquiry" : "enquiries"}
                    </Typography>
                  )}
                </Box>
              );
            })()}
          </Box>

          {/* Quick action toolbar — individual square glass buttons
              spread across the hero's right edge (reference design).
              Each button is 42×42 with a subtle frosted border, so the
              hero reads as an executive command rail, not a clustered pill.
              The "Open calendar" tile is a pure navigation action so it
              stays available; the three mutation tiles disable in
              read-only historical mode. */}
          <Stack direction="row" gap={1}>
            {(() => {
              /* Single source of truth for the header command rail.
                 Each tile owns its enabled-state + tooltip locally so
                 the row map below stays purely visual. `dataMissing`
                 means "the lead doesn't have what this action needs"
                 (no phone for Call, no email for Email) — distinct
                 from `readonlyDisabled` which is access-control. */
              const phoneRaw = (lead?.phone ?? "").trim();
              const emailRaw = (lead?.email ?? "").trim();
              return [
                {
                  title: "Edit lead",
                  icon: <EditOutlinedIcon sx={{ fontSize: 18 }} />,
                  onClick: () => setEditOpen(true),
                  readonlyDisabled: true,
                  dataMissing: false,
                  dataTooltip: "",
                  loading: false,
                },
                {
                  title: phoneRaw ? `Call ${phoneRaw}` : "Call customer",
                  icon: <PhoneInTalkIcon sx={{ fontSize: 18 }} />,
                  onClick: () => handleCallAction(phoneRaw),
                  readonlyDisabled: true,
                  dataMissing: !phoneRaw,
                  dataTooltip: "Phone number not available",
                  loading: false,
                },
                {
                  title: emailRaw ? `Email ${emailRaw}` : "Email customer",
                  icon: emailLoading
                    ? <CircularProgress size={14} sx={{ color: "inherit" }} />
                    : <MailOutlineIcon sx={{ fontSize: 18 }} />,
                  onClick: () => handleEmailAction({
                    email: emailRaw,
                    name: lead?.name ?? null,
                    threadId: lead?.id ?? null,
                    onSent: () => loadAll(),
                  }),
                  readonlyDisabled: true,
                  dataMissing: !emailRaw,
                  dataTooltip: "No email address available",
                  loading: emailLoading,
                },
                {
                  title: "Add internal note",
                  icon: <NoteAddIcon sx={{ fontSize: 18 }} />,
                  onClick: () => { setHeaderNoteText(""); setHeaderNoteOpen(true); },
                  readonlyDisabled: true,
                  dataMissing: false,
                  dataTooltip: "",
                  loading: false,
                },
                {
                  title: "Schedule follow-up",
                  icon: <EventIcon sx={{ fontSize: 18 }} />,
                  onClick: () => { setHeaderSchedDate(""); setHeaderSchedNotes(""); setHeaderSchedPriority("medium"); setHeaderScheduleOpen(true); },
                  readonlyDisabled: true,
                  dataMissing: false,
                  dataTooltip: "",
                  loading: false,
                },
              ];
            })().map((b, i) => {
              const accessDisabled = b.readonlyDisabled && !canAct;
              const disabled = accessDisabled || b.dataMissing || b.loading;
              const tip = accessDisabled
                ? "Read-only historical access"
                : (b.dataMissing ? b.dataTooltip : b.title);
              return (
                <Tooltip key={i} title={tip}>
                  {/* MUI rule: disabled buttons must be wrapped in a
                      span or the tooltip won't fire. */}
                  <span>
                    <IconButton
                      onClick={b.onClick}
                      disabled={disabled}
                      sx={{
                        width: 42, height: 42, borderRadius: "10px",
                        color: "#fff",
                        bgcolor: "rgba(255,255,255,0.05)",
                        border: "1px solid rgba(255,255,255,0.10)",
                        backdropFilter: "blur(8px)",
                        boxShadow: "0 1px 0 rgba(255,255,255,0.06) inset, 0 2px 8px rgba(0,0,0,0.12)",
                        transition: "all 0.18s",
                        "&:hover": {
                          bgcolor: "rgba(255,255,255,0.12)",
                          borderColor: "rgba(255,255,255,0.20)",
                          color: GOLD,
                        },
                        "&.Mui-disabled": {
                          color: "rgba(255,255,255,0.30)",
                          bgcolor: "rgba(255,255,255,0.02)",
                          borderColor: "rgba(255,255,255,0.06)",
                        },
                      }}
                    >
                      {b.icon}
                    </IconButton>
                  </span>
                </Tooltip>
              );
            })}
          </Stack>
          {canManageResolution && (
            <Button
              onClick={(e) => setActionMenuAnchor(e.currentTarget)}
              endIcon={<DropdownIcon sx={{ fontSize: "18px !important", ml: -0.5 }} />}
              sx={{
                ml: 1, textTransform: "none", fontWeight: 600, fontSize: 13,
                color: "#fff", bgcolor: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.10)",
                borderRadius: "10px",
                px: 1.8, height: 42,
                backdropFilter: "blur(8px)",
                boxShadow: "0 1px 0 rgba(255,255,255,0.06) inset, 0 2px 8px rgba(0,0,0,0.12)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.12)", borderColor: "rgba(255,255,255,0.20)" },
              }}
            >
              More
            </Button>
          )}
        </Stack>
        </Paper>
      </Box>

      {/* ── Resolution banner — shown when this lead is merged/archived/duplicate.
           Under the archive-only merge contract the source keeps every note /
           email / task / activity; the banner clarifies that explicitly so the
           operator knows the data still lives here, not on the target. ── */}
      {(lead.merged_into_lead_id || lead.archived_at) && (
        <Box
          sx={{
            px: 3, py: 1.5,
            bgcolor: lead.merged_into_lead_id ? "#f3e8ff" : "#fef3c7",
            borderBottom: `2px solid ${lead.merged_into_lead_id ? "#a855f7" : "#f59e0b"}`,
          }}
        >
          <Stack direction="row" alignItems="flex-start" gap={1.5}>
            {lead.merged_into_lead_id ? <MergeTypeIcon sx={{ color: "#7c3aed", mt: 0.2 }} /> : <ArchiveIcon sx={{ color: "#b45309", mt: 0.2 }} />}
            <Box flex={1}>
              {lead.merged_into_lead_id ? (
                <>
                  <Typography variant="body2" fontWeight={700} color={NAVY}>
                    This lead was merged into{" "}
                    <Box
                      component="span"
                      sx={{ color: "#7c3aed", textDecoration: "underline", cursor: "pointer" }}
                      onClick={() => navigate(`/leads/${lead.merged_into_lead_id}`)}
                    >
                      Lead #{lead.merged_into_lead_id}
                    </Box>
                  </Typography>
                  <Typography variant="caption" sx={{ display: "block", color: "#475569", mt: 0.3, fontSize: 11.5 }}>
                    Archived and removed from the active pipeline. All notes, emails, tasks, and timeline history remain on this lead — nothing was moved.
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" fontWeight={700} color={NAVY}>
                  {lead.resolution_status === "duplicate" ? "Marked as duplicate" : "Archived"}
                  {lead.archived_reason ? ` · ${lead.archived_reason}` : ""}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.2 }}>
                {lead.archived_at && `Archived ${new Date(lead.archived_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true })}`}
              </Typography>
            </Box>
            {canManageResolution && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<UnarchiveIcon sx={{ fontSize: 16 }} />}
                onClick={handleRestore}
                sx={{ textTransform: "none", borderColor: NAVY, color: NAVY, "&:hover": { bgcolor: "rgba(0,0,0,0.04)", borderColor: NAVY } }}
              >
                Restore
              </Button>
            )}
          </Stack>
        </Box>
      )}

      {/* ── Admin actions menu ─────────────────────────────────────── */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={!!actionMenuAnchor}
        onClose={() => setActionMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {/* "Merge into another lead" — temporarily hidden while the
            archive-only merge contract is being finalised. The
            MergeLeadDialog, openMergeDialog, and handleConfirmMerge wiring
            stay in place; strip the comment markers below to re-enable.
        {!lead.merged_into_lead_id && !lead.archived_at && (
          <MenuItem onClick={openMergeDialog}>
            <MergeTypeIcon sx={{ fontSize: 18, mr: 1, color: "#7c3aed" }} /> Merge into another lead…
          </MenuItem>
        )}
        */}
        {/* "Mark as duplicate" — removed per spec. The same outcome (link
            this lead to a primary as a same-event duplicate) is now
            covered by the richer "Flag as same event" picker below.
            openArchiveDialog + ContentCopyIcon are still referenced by
            other archive actions so we don't remove them. */}
        {!lead.merged_into_lead_id && !lead.archived_at && (
          <MenuItem onClick={() => openArchiveDialog("archived", "Archive lead")}>
            <ArchiveIcon sx={{ fontSize: 18, mr: 1, color: "#b45309" }} /> Archive lead…
          </MenuItem>
        )}
        {/* Same-event grouping ─ operator-driven, no INSERT-time blocking. */}
        {!lead.merged_into_lead_id && !lead.archived_at && !lead.is_primary_event && (
          <MenuItem onClick={handleMarkPrimary}>
            <Box component="span" sx={{ display: "inline-flex", justifyContent: "center", width: 18, mr: 1, color: "#b45309", fontWeight: 800 }}>★</Box>
            Mark as primary event
          </MenuItem>
        )}
        {!lead.merged_into_lead_id && !lead.archived_at && (
          <MenuItem onClick={openSameEventDialog}>
            <Box component="span" sx={{ display: "inline-flex", justifyContent: "center", width: 18, mr: 1, color: "#6d28d9", fontWeight: 800 }}>≡</Box>
            Flag as same event…
          </MenuItem>
        )}
        {!lead.merged_into_lead_id && !lead.archived_at && (
          <MenuItem onClick={handleMoveToTrash}>
            <Box component="span" sx={{ display: "inline-flex", justifyContent: "center", width: 18, mr: 1, color: "#64748b", fontWeight: 800 }}>🗑</Box>
            Move to trash
          </MenuItem>
        )}
        {(lead.merged_into_lead_id || lead.archived_at) && (
          <MenuItem onClick={handleRestore}>
            <UnarchiveIcon sx={{ fontSize: 18, mr: 1, color: "#10b981" }} /> Restore to active
          </MenuItem>
        )}
      </Menu>

      {/* ── Archive/Mark-duplicate dialog ──────────────────────────── */}
      <Dialog open={!!archiveDialog} onClose={() => setArchiveDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>
          {archiveDialog?.title}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            This hides the lead from active dashboards and cancels open follow-ups. Nothing is deleted — it can be restored later.
          </Typography>
          <TextField
            fullWidth multiline rows={3} size="small"
            placeholder="Reason (optional)"
            value={archiveReason}
            onChange={(e) => setArchiveReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveDialog(null)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button onClick={confirmArchive} variant="contained" sx={{ textTransform: "none", bgcolor: NAVY, "&:hover": { bgcolor: "#2d5f8a" } }}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Merge dialog — searchable autocomplete + confirm summary.
            Target-selection UX only; the merge handler still calls the
            existing mergeLead() service, so business logic / activity
            transfer / DB flow are unchanged. */}
      <MergeLeadDialog
        open={mergeDialogOpen}
        sourceLead={lead}
        onClose={() => setMergeDialogOpen(false)}
        onConfirm={handleConfirmMerge}
      />

      {/* ── Flag-same-event dialog ──────────────────────────────────────
            Searchable Autocomplete + match-reason badges + chip-grid
            reason selector. Same business contract — backend just runs
            flagLeadSameEvent which archives the source lead as
            resolution_status='same_event_duplicate'. No data is moved;
            history is preserved; the lead can be restored. */}
      <FlagSameEventDialog
        open={sameEventDialogOpen}
        sourceLead={lead}
        onClose={() => setSameEventDialogOpen(false)}
        onConfirm={handleConfirmSameEvent}
      />

      {/* ── Task detail dialog (Open · Reassign · In-progress · Edit) ── */}
      <TaskDetailDialog
        open={!!detailTask}
        task={detailTask}
        teamUsers={teamUsers}
        onClose={() => setDetailTask(null)}
        onChanged={() => { loadAll(); setDetailTask(null); }}
      />

      {/* ══════════════════════════════════════════════════════════════
          Main Workspace — top-down enterprise CRM layout.
            1. Customer Info Strip (horizontal info pills)
            2. Stats Row (6 metric cards)
            3. 2-column CRM workspace (~65 / 35 split):
                 LEFT  → Operational workspace stack:
                         Related Enquiries · Lead Management ·
                         Quick Actions · Follow-up Management
                 RIGHT → Sticky Timeline activity rail
                         (~400px, scrolls internally)
          The flex container becomes a vertical scroll surface; the
          old 3-column split (sidebar + center + right) is retired.
          ══════════════════════════════════════════════════════════════ */}
      <Box sx={{ flex: 1, overflow: "auto", bgcolor: SURFACE.bg }}>
        <Box sx={{ maxWidth: 1700, mx: "auto", px: { xs: 1.6, md: 2.4 }, py: { xs: 1.6, md: 2 } }}>

          {/* ════ CUSTOMER INFO STRIP ════════════════════════════════ */}
          <Paper elevation={0} sx={{
            mb: 1.6, px: 2.2, py: 1.4,
            borderRadius: SURFACE.cardRadius,
            border: SURFACE.cardBorder,
            bgcolor: SURFACE.card,
            boxShadow: SURFACE.cardShadow,
          }}>
            <Stack
              direction="row" alignItems="center" gap={2}
              divider={<Divider orientation="vertical" flexItem sx={{ borderColor: "#eef1f5" }} />}
              flexWrap="wrap"
            >
              <InfoCell
                icon={<PersonIcon sx={{ fontSize: 18 }} />}
                label="Inquiry Contact"
                value={lead.name}
                capitalize
                subtitle={`Inquiry ${new Date(lead.created_at).toLocaleString("en-US", { month: "short", year: "numeric" })}`}
              />
              <InfoCell icon={<EmailIcon sx={{ fontSize: 18 }} />} label="Email" value={lead.email} mono />
              <InfoCell icon={<PhoneIcon sx={{ fontSize: 18 }} />} label="Phone" value={lead.phone} mono />
              <InfoCell
                icon={<EventIcon sx={{ fontSize: 18 }} />}
                label="Event"
                value={lead.event_type?.replace(/_/g, " ")}
                capitalize
                subtitle={lead.event_date ? formatEventDate(lead.event_date) : undefined}
              />
              <InfoCell icon={<PeopleIcon sx={{ fontSize: 18 }} />} label="Guests" value={lead.guest_count?.toString()} />
              <InfoCell icon={<MoneyIcon sx={{ fontSize: 18 }} />} label="Budget" value={formatLeadBudget(lead)} />
              <Box flex={1} />
              {lead.customer_id && (
                <Button
                  variant="outlined" size="small"
                  startIcon={<PersonIcon sx={{ fontSize: 16 }} />}
                  onClick={() => navigate(`/customer-history/${lead.customer_id}`)}
                  sx={{
                    textTransform: "none", fontWeight: 700, borderRadius: 1.5,
                    borderColor: NAVY, color: NAVY,
                    "&:hover": { bgcolor: `${NAVY}08`, borderColor: NAVY },
                  }}
                >
                  Customer Profile
                </Button>
              )}
            </Stack>
          </Paper>

          {/* ════ STATS ROW + INLINE DETAIL PANEL ════════════════════
              All 6 cards are click-targets that toggle an expandable
              detail panel directly below them. Click a second time (or
              the close X) collapses. Active card lifts + glows; others
              fade so the operator always knows which lens is active. */}
          {(() => {
            const allLeads       = [lead, ...relatedLeads];
            const activeLeads    = allLeads.filter((l) => !l.archived_at && !l.merged_into_lead_id);
            const archivedLeads  = allLeads.filter((l) =>  l.archived_at ||  l.merged_into_lead_id);
            const sameEventLeads = allLeads.filter((l) => l.resolution_status === "same_event_duplicate");
            const primaryEvent   = activeLeads.find((l) => l.is_primary_event) ?? null;
            const quoteLeads     = activeLeads.filter((l) => ["ready_for_quote","ready_for_booking","confirmed"].includes(l.status));
            const bookingLeads   = allLeads.filter((l) => l.status === "confirmed");

            type Card = {
              id: string; icon: React.ReactNode; label: string; value: number; color: string;
              data: LeadThread[]; emptyHint: string;
            };
            const cards: Card[] = [
              { id: "total",      icon: <FlagIcon         sx={{ fontSize: 18 }} />, label: "Total Leads",  value: allLeads.length,       color: NAVY,      data: allLeads,       emptyHint: "No leads on file" },
              { id: "active",     icon: <EventIcon        sx={{ fontSize: 18 }} />, label: "Active Leads", value: activeLeads.length,    color: "#3b82f6", data: activeLeads,    emptyHint: "No active enquiries" },
              { id: "archived",   icon: <ArchiveIcon      sx={{ fontSize: 18 }} />, label: "Archived",     value: archivedLeads.length,  color: "#94a3b8", data: archivedLeads,  emptyHint: "Nothing archived" },
              { id: "same_event", icon: <PeopleIcon       sx={{ fontSize: 18 }} />, label: "Same Event",   value: sameEventLeads.length, color: "#a78bfa", data: sameEventLeads, emptyHint: "No same-event duplicates" },
              { id: "quotes",     icon: <MoneyIcon        sx={{ fontSize: 18 }} />, label: "Quotes",       value: quoteLeads.length,     color: "#f59e0b", data: quoteLeads,     emptyHint: "No leads in quote-ready stage" },
              { id: "bookings",   icon: <CheckCircleIcon  sx={{ fontSize: 18 }} />, label: "Bookings",     value: bookingLeads.length,   color: "#16a34a", data: bookingLeads,   emptyHint: "No confirmed bookings" },
            ];
            const activeCard = cards.find((c) => c.id === activeStatCard) ?? null;

            // ── Lead mini-card used inside the detail panel ────────
            const LeadMini = ({ l }: { l: LeadThread }) => {
              const sc = (STATUS_CONFIG as Record<string, { label: string; color: string }>)[l.status] || { label: l.status, color: "#94a3b8" };
              const isCurrent = l.id === lead.id;
              const isArchived = !!(l.archived_at || l.merged_into_lead_id);
              const isPrimary  = !!l.is_primary_event;
              return (
                <Box
                  onClick={(e) => { e.stopPropagation(); navigate(`/leads/${l.id}`); }}
                  sx={{
                    cursor: "pointer", p: 1.3, borderRadius: 1.5,
                    border: isCurrent ? `1.5px solid ${GOLD}` : isPrimary ? `1.5px solid ${GOLD}99` : "1px solid #e5e7eb",
                    bgcolor: isCurrent ? "#fffbeb" : isArchived ? "#fafbfc" : "#fff",
                    transition: "all 0.15s",
                    "&:hover": { borderColor: NAVY, boxShadow: "0 2px 10px rgba(13,27,42,0.08)", transform: "translateY(-1px)" },
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={0.5} mb={0.5} flexWrap="wrap">
                    <LeadNumberBadge value={l.lead_number ?? `#${l.id}`} variant="muted" />
                    {isPrimary && (
                      <Box sx={{ fontSize: 9, fontWeight: 800, color: "#b45309", bgcolor: "#fef3c7", border: "1px solid #fde68a", borderRadius: 0.6, px: 0.5, py: 0.05, letterSpacing: 0.4 }}>
                        ★ PRIMARY
                      </Box>
                    )}
                    {isCurrent && (
                      <Box sx={{ fontSize: 9, fontWeight: 800, color: "#fff", bgcolor: NAVY, borderRadius: 0.6, px: 0.5, py: 0.05, letterSpacing: 0.4 }}>
                        VIEWING
                      </Box>
                    )}
                    <Box sx={{
                      fontSize: 9, fontWeight: 800, letterSpacing: 0.4,
                      bgcolor: `${sc.color}22`, color: sc.color,
                      border: `1px solid ${sc.color}55`, borderRadius: 0.6,
                      px: 0.5, py: 0.05, textTransform: "uppercase",
                    }}>
                      {sc.label}
                    </Box>
                  </Stack>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: NAVY, textTransform: "capitalize", lineHeight: 1.3 }}>
                    {l.event_type?.replace(/_/g, " ") ?? "—"}
                    {l.event_date && ` · ${formatEventDate(l.event_date)}`}
                  </Typography>
                  <Stack direction="row" gap={1} mt={0.4} flexWrap="wrap" alignItems="center">
                    {l.guest_count != null && (
                      <Typography sx={{ fontSize: 10.5, color: "#64748b", fontWeight: 600 }}>
                        {l.guest_count} guests
                      </Typography>
                    )}
                    {l.budget_value != null && Number(l.budget_value) > 0 && (
                      <Typography sx={{ fontSize: 10.5, color: "#16a34a", fontWeight: 700 }}>
                        ${Number(l.budget_value).toLocaleString()}
                      </Typography>
                    )}
                    {l.preferred_hall && (
                      <Typography sx={{ fontSize: 10.5, color: "#64748b", fontWeight: 600 }}>
                        {l.preferred_hall}
                      </Typography>
                    )}
                  </Stack>
                  <Typography sx={{ fontSize: 10, color: "#94a3b8", mt: 0.4 }}>
                    {isArchived && l.archived_at
                      ? `Archived ${formatShortDate(l.archived_at)}`
                      : `Created ${formatShortDate(l.created_at)}`}
                    {l.archived_reason ? ` · ${l.archived_reason}` : ""}
                  </Typography>
                </Box>
              );
            };

            // ── Quote/Booking financial summary helpers ───────────
            const quoteSum = quoteLeads.reduce((s, l) => s + Number(l.budget_value ?? 0), 0);
            const quoteMin = quoteLeads.length ? Math.min(...quoteLeads.map((l) => Number(l.budget_value ?? 0)).filter((v) => v > 0)) : 0;
            const quoteMax = quoteLeads.length ? Math.max(...quoteLeads.map((l) => Number(l.budget_value ?? 0))) : 0;
            const bookingSum = bookingLeads.reduce((s, l) => s + Number(l.budget_value ?? 0), 0);

            return (
              <>
                {/* ── 6-card grid (click-to-toggle) ──────────── */}
                <Box sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(6, 1fr)" },
                  gap: 1.2, mb: activeCard ? 1.2 : 1.6,
                }}>
                  {cards.map((c) => {
                    const isActive = activeStatCard === c.id;
                    const isDimmed = activeStatCard !== null && !isActive;
                    return (
                      <Paper
                        key={c.id} elevation={0}
                        role="button" tabIndex={0}
                        onClick={() => setActiveStatCard((prev) => (prev === c.id ? null : c.id))}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setActiveStatCard((prev) => (prev === c.id ? null : c.id));
                          }
                        }}
                        sx={{
                          cursor: "pointer", position: "relative", userSelect: "none",
                          px: 1.6, py: 1.3, borderRadius: SURFACE.cardRadius,
                          border: isActive ? `1px solid ${c.color}55` : SURFACE.cardBorder,
                          bgcolor: SURFACE.card,
                          opacity: isDimmed ? 0.55 : 1,
                          transform: isActive ? "translateY(-1px)" : "none",
                          boxShadow: isActive
                            ? `0 8px 22px ${c.color}24, 0 0 0 3px ${c.color}10`
                            : SURFACE.cardShadow,
                          transition: "all 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": isDimmed ? {} : {
                            transform: "translateY(-1px)",
                            boxShadow: isActive
                              ? `0 8px 22px ${c.color}24, 0 0 0 3px ${c.color}10`
                              : SURFACE.cardHover,
                            borderColor: `${c.color}55`,
                          },
                          "&:focus-visible": {
                            outline: "none",
                            boxShadow: `0 0 0 3px ${c.color}55`,
                          },
                        }}
                      >
                        <Stack direction="row" alignItems="center" gap={1.1}>
                          <Box sx={{
                            width: 32, height: 32, borderRadius: "9px",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            bgcolor: isActive ? c.color : `${c.color}12`,
                            color: isActive ? "#fff" : c.color,
                            flexShrink: 0,
                            transition: "all 0.2s",
                            boxShadow: isActive ? `0 4px 10px ${c.color}33` : "none",
                            "& svg": { fontSize: 18 },
                          }}>
                            {c.icon}
                          </Box>
                          <Box minWidth={0}>
                            <Typography sx={{ fontSize: 22, fontWeight: 800, color: NAVY, lineHeight: 1, letterSpacing: "-0.5px" }}>
                              {c.value}
                            </Typography>
                            <Typography sx={{
                              fontSize: 9.5, fontWeight: 700, color: isActive ? c.color : "#94a3b8",
                              letterSpacing: 0.6, textTransform: "uppercase", mt: 0.4, whiteSpace: "nowrap",
                            }}>
                              {c.label}
                            </Typography>
                          </Box>
                        </Stack>
                        {/* Down-pointer arrow when active */}
                        {isActive && (
                          <Box sx={{
                            position: "absolute", bottom: -7, left: "50%",
                            width: 12, height: 12, bgcolor: "#fff",
                            border: `2px solid ${c.color}`,
                            borderTop: "none", borderLeft: "none",
                            transform: "translateX(-50%) rotate(45deg)",
                            zIndex: 1,
                          }} />
                        )}
                      </Paper>
                    );
                  })}
                </Box>

                {/* ── Inline detail panel ─────────────────────── */}
                <Collapse in={activeCard !== null} timeout={250}>
                  {activeCard && (
                    <Paper elevation={0} sx={{
                      mb: 2, borderRadius: 2,
                      border: `1.5px solid ${activeCard.color}40`,
                      bgcolor: "#fff",
                      boxShadow: `0 6px 20px ${activeCard.color}18`,
                      overflow: "hidden",
                    }}>
                      {/* Panel header */}
                      <Box sx={{
                        px: 2.2, py: 1.4,
                        background: `linear-gradient(135deg, ${activeCard.color}14 0%, ${activeCard.color}05 100%)`,
                        borderBottom: `1px solid ${activeCard.color}22`,
                      }}>
                        <Stack direction="row" alignItems="center" gap={1.2}>
                          <Box sx={{
                            width: 30, height: 30, borderRadius: 1.2,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            bgcolor: activeCard.color, color: "#fff",
                          }}>
                            {activeCard.icon}
                          </Box>
                          <Box flex={1} minWidth={0}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: NAVY, lineHeight: 1.2, textTransform: "uppercase", letterSpacing: 0.6 }}>
                              {activeCard.label}
                              <Box component="span" sx={{ ml: 0.8, color: activeCard.color, fontSize: 13 }}>
                                ({activeCard.value})
                              </Box>
                            </Typography>
                            <Typography sx={{ fontSize: 10.5, color: "#64748b", mt: 0.2 }}>
                              {activeCard.id === "total"      && "All enquiries linked to this customer — active, archived, and same-event"}
                              {activeCard.id === "active"     && "Live enquiries currently in pipeline"}
                              {activeCard.id === "archived"   && "Hidden from pipeline — preserved in customer history"}
                              {activeCard.id === "same_event" && "Operator-flagged duplicates grouped to a primary enquiry"}
                              {activeCard.id === "quotes"     && "Leads in quote-ready, booking-ready, or confirmed stage"}
                              {activeCard.id === "bookings"   && "Confirmed deals — payment & hall assignment status"}
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={() => setActiveStatCard(null)} sx={{ color: "#64748b", "&:hover": { color: NAVY } }}>
                            <CloseIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Stack>
                      </Box>

                      {/* Panel body */}
                      <Box sx={{ p: 2 }}>
                        {/* Empty state */}
                        {activeCard.data.length === 0 && activeCard.id !== "same_event" && (
                          <Box textAlign="center" py={3}>
                            <Box sx={{
                              width: 48, height: 48, borderRadius: "50%",
                              bgcolor: `${activeCard.color}15`, color: activeCard.color,
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              mb: 1.2,
                            }}>
                              {activeCard.icon}
                            </Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
                              {activeCard.emptyHint}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.4 }}>
                              When records exist, they'll appear here for quick triage.
                            </Typography>
                          </Box>
                        )}

                        {/* ── SAME EVENT — relationship graph ─── */}
                        {activeCard.id === "same_event" && (
                          <>
                            {!primaryEvent && sameEventLeads.length === 0 ? (
                              <Box textAlign="center" py={3}>
                                <Box sx={{ width: 48, height: 48, borderRadius: "50%", bgcolor: "#a78bfa15", color: "#a78bfa", display: "inline-flex", alignItems: "center", justifyContent: "center", mb: 1.2 }}>
                                  <PeopleIcon sx={{ fontSize: 22 }} />
                                </Box>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
                                  No same-event group yet
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.4 }}>
                                  Flag a duplicate enquiry as "Same Event" on its card to start grouping.
                                </Typography>
                              </Box>
                            ) : (
                              <Box>
                                {/* Primary at top */}
                                {primaryEvent ? (
                                  <Box mb={sameEventLeads.length > 0 ? 1.5 : 0}>
                                    <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: "#b45309", letterSpacing: 0.6, textTransform: "uppercase", mb: 0.6 }}>
                                      ★ Primary Event
                                    </Typography>
                                    <LeadMini l={primaryEvent} />
                                  </Box>
                                ) : (
                                  <Box mb={1.5} sx={{ p: 1.2, borderRadius: 1.5, bgcolor: "#fef3c7", border: "1px dashed #fde68a" }}>
                                    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#b45309" }}>
                                      No primary event set — pick one with <b>Set Primary</b> on a related card below.
                                    </Typography>
                                  </Box>
                                )}

                                {/* Connector */}
                                {primaryEvent && sameEventLeads.length > 0 && (
                                  <Stack direction="row" alignItems="center" gap={1} sx={{ pl: 2, py: 0.8 }}>
                                    <Box sx={{ width: 2, height: 18, bgcolor: "#a78bfa" }} />
                                    <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: "#6d28d9", letterSpacing: 0.6, textTransform: "uppercase" }}>
                                      {sameEventLeads.length} linked duplicate{sameEventLeads.length === 1 ? "" : "s"} ↓
                                    </Typography>
                                  </Stack>
                                )}

                                {/* Same-event children */}
                                {sameEventLeads.length > 0 && (
                                  <Box sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                                    gap: 1.2,
                                  }}>
                                    {sameEventLeads.map((l) => <LeadMini key={l.id} l={l} />)}
                                  </Box>
                                )}
                              </Box>
                            )}
                          </>
                        )}

                        {/* ── QUOTES — financial summary + list ─── */}
                        {activeCard.id === "quotes" && quoteLeads.length > 0 && (
                          <>
                            <Box sx={{
                              display: "grid",
                              gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
                              gap: 1.2, mb: 1.8,
                            }}>
                              <Box sx={{ p: 1.2, borderRadius: 1.5, bgcolor: "#fffbeb", border: "1px solid #fde68a" }}>
                                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: "#b45309", letterSpacing: 0.4, textTransform: "uppercase" }}>
                                  Quote Pipeline
                                </Typography>
                                <Typography sx={{ fontSize: 17, fontWeight: 800, color: NAVY, mt: 0.3 }}>
                                  ${quoteSum.toLocaleString()}
                                </Typography>
                              </Box>
                              <Box sx={{ p: 1.2, borderRadius: 1.5, bgcolor: "#fafbfc", border: "1px solid #e5e7eb" }}>
                                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: "#64748b", letterSpacing: 0.4, textTransform: "uppercase" }}>
                                  Lowest
                                </Typography>
                                <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#16a34a", mt: 0.3 }}>
                                  ${(quoteMin || 0).toLocaleString()}
                                </Typography>
                              </Box>
                              <Box sx={{ p: 1.2, borderRadius: 1.5, bgcolor: "#fafbfc", border: "1px solid #e5e7eb" }}>
                                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: "#64748b", letterSpacing: 0.4, textTransform: "uppercase" }}>
                                  Highest
                                </Typography>
                                <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#ef4444", mt: 0.3 }}>
                                  ${quoteMax.toLocaleString()}
                                </Typography>
                              </Box>
                              <Box sx={{ p: 1.2, borderRadius: 1.5, bgcolor: "#fafbfc", border: "1px solid #e5e7eb" }}>
                                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: "#64748b", letterSpacing: 0.4, textTransform: "uppercase" }}>
                                  Spread
                                </Typography>
                                <Typography sx={{ fontSize: 15, fontWeight: 800, color: NAVY, mt: 0.3 }}>
                                  ${Math.max(0, quoteMax - (quoteMin || 0)).toLocaleString()}
                                </Typography>
                              </Box>
                            </Box>
                            <Stack gap={0.8}>
                              {quoteLeads.map((l) => {
                                const sc = (STATUS_CONFIG as Record<string, { label: string; color: string }>)[l.status] || { label: l.status, color: "#94a3b8" };
                                const amount = Number(l.budget_value ?? 0);
                                return (
                                  <Stack
                                    key={l.id} direction="row" alignItems="center" gap={1}
                                    onClick={() => navigate(`/leads/${l.id}`)}
                                    sx={{
                                      cursor: "pointer", p: 1.2, borderRadius: 1.5,
                                      border: l.id === lead.id ? `1.5px solid ${GOLD}` : "1px solid #e5e7eb",
                                      bgcolor: l.id === lead.id ? "#fffbeb" : "#fff",
                                      transition: "all 0.15s",
                                      "&:hover": { borderColor: "#f59e0b", boxShadow: "0 2px 10px rgba(245,158,11,0.18)" },
                                    }}
                                  >
                                    <LeadNumberBadge value={l.lead_number ?? `#${l.id}`} variant="muted" />
                                    <Box flex={1} minWidth={0}>
                                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: NAVY, textTransform: "capitalize" }} noWrap>
                                        {l.event_type?.replace(/_/g, " ") ?? "—"}
                                        {l.event_date && ` · ${formatEventDate(l.event_date)}`}
                                      </Typography>
                                      <Stack direction="row" gap={0.7} mt={0.2} alignItems="center" flexWrap="wrap">
                                        <Box sx={{
                                          fontSize: 9, fontWeight: 800, letterSpacing: 0.4,
                                          bgcolor: `${sc.color}22`, color: sc.color,
                                          border: `1px solid ${sc.color}55`, borderRadius: 0.6,
                                          px: 0.5, py: 0.05, textTransform: "uppercase",
                                        }}>
                                          {sc.label}
                                        </Box>
                                        {l.guest_count != null && (
                                          <Typography sx={{ fontSize: 10.5, color: "#64748b", fontWeight: 600 }}>
                                            {l.guest_count} guests
                                          </Typography>
                                        )}
                                      </Stack>
                                    </Box>
                                    <Box textAlign="right">
                                      <Typography sx={{ fontSize: 15, fontWeight: 800, color: amount > 0 ? "#16a34a" : "#94a3b8", lineHeight: 1 }}>
                                        {amount > 0 ? `$${amount.toLocaleString()}` : "—"}
                                      </Typography>
                                      <Typography sx={{ fontSize: 9.5, color: "#94a3b8", mt: 0.3, letterSpacing: 0.3 }}>
                                        QUOTE AMOUNT
                                      </Typography>
                                    </Box>
                                  </Stack>
                                );
                              })}
                            </Stack>
                          </>
                        )}

                        {/* ── BOOKINGS — confirmed leads ────────── */}
                        {activeCard.id === "bookings" && bookingLeads.length > 0 && (
                          <>
                            <Box sx={{
                              display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                              gap: 1.2, mb: 1.8,
                            }}>
                              <Box sx={{ p: 1.2, borderRadius: 1.5, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: "#16a34a", letterSpacing: 0.4, textTransform: "uppercase" }}>
                                  Confirmed Revenue
                                </Typography>
                                <Typography sx={{ fontSize: 17, fontWeight: 800, color: NAVY, mt: 0.3 }}>
                                  ${bookingSum.toLocaleString()}
                                </Typography>
                              </Box>
                              <Box sx={{ p: 1.2, borderRadius: 1.5, bgcolor: "#fafbfc", border: "1px solid #e5e7eb" }}>
                                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: "#64748b", letterSpacing: 0.4, textTransform: "uppercase" }}>
                                  Total Bookings
                                </Typography>
                                <Typography sx={{ fontSize: 17, fontWeight: 800, color: NAVY, mt: 0.3 }}>
                                  {bookingLeads.length}
                                </Typography>
                              </Box>
                              <Box sx={{ p: 1.2, borderRadius: 1.5, bgcolor: "#fafbfc", border: "1px solid #e5e7eb" }}>
                                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: "#64748b", letterSpacing: 0.4, textTransform: "uppercase" }}>
                                  Avg Deal Size
                                </Typography>
                                <Typography sx={{ fontSize: 17, fontWeight: 800, color: NAVY, mt: 0.3 }}>
                                  ${bookingLeads.length ? Math.round(bookingSum / bookingLeads.length).toLocaleString() : 0}
                                </Typography>
                              </Box>
                            </Box>
                            <Stack gap={0.8}>
                              {bookingLeads.map((l) => {
                                const amount = Number(l.budget_value ?? 0);
                                return (
                                  <Stack
                                    key={l.id} direction="row" alignItems="center" gap={1}
                                    onClick={() => navigate(`/leads/${l.id}`)}
                                    sx={{
                                      cursor: "pointer", p: 1.2, borderRadius: 1.5,
                                      border: l.id === lead.id ? `1.5px solid ${GOLD}` : "1px solid #bbf7d0",
                                      bgcolor: l.id === lead.id ? "#fffbeb" : "#f0fdf4",
                                      transition: "all 0.15s",
                                      "&:hover": { borderColor: "#16a34a", boxShadow: "0 2px 10px rgba(22,163,74,0.18)" },
                                    }}
                                  >
                                    <CheckCircleIcon sx={{ fontSize: 22, color: "#16a34a", flexShrink: 0 }} />
                                    <Box flex={1} minWidth={0}>
                                      <Stack direction="row" alignItems="center" gap={0.6} flexWrap="wrap">
                                        <LeadNumberBadge value={l.lead_number ?? `#${l.id}`} variant="muted" />
                                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: NAVY, textTransform: "capitalize" }} noWrap>
                                          {l.event_type?.replace(/_/g, " ") ?? "—"}
                                        </Typography>
                                      </Stack>
                                      <Stack direction="row" gap={0.8} mt={0.3} flexWrap="wrap" alignItems="center">
                                        {l.event_date && (
                                          <Typography sx={{ fontSize: 10.5, color: "#475569", fontWeight: 700 }}>
                                            📅 {formatEventDate(l.event_date)}
                                          </Typography>
                                        )}
                                        {l.preferred_hall && (
                                          <Typography sx={{ fontSize: 10.5, color: "#475569", fontWeight: 700 }}>
                                            🏛 {l.preferred_hall}
                                          </Typography>
                                        )}
                                        {l.guest_count != null && (
                                          <Typography sx={{ fontSize: 10.5, color: "#64748b", fontWeight: 600 }}>
                                            👥 {l.guest_count}
                                          </Typography>
                                        )}
                                        <Box sx={{ fontSize: 9, fontWeight: 800, color: "#fff", bgcolor: "#16a34a", borderRadius: 0.6, px: 0.6, py: 0.05, letterSpacing: 0.4 }}>
                                          CONFIRMED
                                        </Box>
                                        <Box sx={{ fontSize: 9, fontWeight: 800, color: "#b45309", bgcolor: "#fef3c7", border: "1px solid #fde68a", borderRadius: 0.6, px: 0.6, py: 0.05, letterSpacing: 0.4 }}>
                                          PAYMENT · TBD
                                        </Box>
                                      </Stack>
                                    </Box>
                                    <Box textAlign="right">
                                      <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#16a34a", lineHeight: 1 }}>
                                        {amount > 0 ? `$${amount.toLocaleString()}` : "—"}
                                      </Typography>
                                      <Typography sx={{ fontSize: 9.5, color: "#94a3b8", mt: 0.3, letterSpacing: 0.3 }}>
                                        FINAL VALUE
                                      </Typography>
                                    </Box>
                                  </Stack>
                                );
                              })}
                            </Stack>
                          </>
                        )}

                        {/* ── TOTAL / ACTIVE / ARCHIVED — lead grid ── */}
                        {(activeCard.id === "total" || activeCard.id === "active" || activeCard.id === "archived") && activeCard.data.length > 0 && (
                          <Box sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                            gap: 1.2,
                          }}>
                            {activeCard.data.map((l) => <LeadMini key={l.id} l={l} />)}
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  )}
                </Collapse>
              </>
            );
          })()}

          {/* ════ 2-COLUMN CRM WORKSPACE ════════════════════════════
              65 / 35 split, modeled after the side activity rails in
              HubSpot deal pages and the Salesforce lead workspace:
                LEFT (~65%) — operational workspace stack. Related
                  Enquiries on top for customer context, then the
                  three-section Lead Actions composer (Lead
                  Management · Quick Actions · Follow-up Management)
                  underneath as equally-sized Papers.
                RIGHT (~35%, 400px) — Timeline activity rail. Sticky
                  at top so the read-only history stays in view while
                  the rep scrolls the operational stack. Renders the
                  shared <LeadActivityTimeline /> with internal
                  scrolling, so the rail itself never grows beyond the
                  viewport.
              On tablet/mobile the columns stack with the operational
              workspace on top and the Timeline rail underneath. */}
          <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 400px" },
            gap: 1.6, alignItems: "start",
          }}>

            {/* ════════════════════════════════════════════════════════
                LEFT (~65%) — Operational workspace

                Stacked top-to-bottom so a rep moves naturally:
                  1. Related Enquiries (customer context)
                  2. Lead Management (status / priority / assignee / VIP)
                  3. Quick Actions (call / note / email / meeting / FU)
                  4. Follow-up Management (pending tasks)
                The lower three are rendered by <LeadActionsPanel /> as
                three equally-sized Papers so the whole column reads as
                one continuous workspace with consistent card widths.
                ════════════════════════════════════════════════════════ */}
            <Stack gap={1.6} minWidth={0}>
              <CustomerRelationshipPanel
                currentLead={lead}
                relatedLeads={relatedLeads}
                canManageResolution={canManageResolution}
                density="comfortable"
                onNavigateLead={(id) => navigate(`/leads/${id}`)}
                onMarkPrimary={async (leadId) => {
                  try {
                    await markLeadAsPrimary(leadId);
                    loadAll();
                    setSnack({ open: true, msg: "Marked as primary event", severity: "success" });
                  } catch (e: any) {
                    setSnack({ open: true, msg: e?.message ?? "Mark primary failed", severity: "error" });
                  }
                }}
                onFlagSameEvent={async (childId, primaryId, reason) => {
                  try {
                    await flagLeadSameEvent(childId, { primary_id: primaryId, reason });
                    loadAll();
                    setSnack({ open: true, msg: `Flagged as same event as #${primaryId}`, severity: "success" });
                  } catch (e: any) {
                    setSnack({ open: true, msg: e?.message ?? "Flag same-event failed", severity: "error" });
                  }
                }}
                onTrash={async (leadId) => {
                  try {
                    await trashLead(leadId);
                    loadAll();
                    setSnack({ open: true, msg: "Lead moved to trash", severity: "success" });
                  } catch (e: any) {
                    setSnack({ open: true, msg: e?.message ?? "Trash failed", severity: "error" });
                  }
                }}
                onRestore={async (leadId) => {
                  try {
                    await restoreLead(leadId);
                    loadAll();
                    setSnack({ open: true, msg: "Lead restored", severity: "success" });
                  } catch (e: any) {
                    setSnack({ open: true, msg: e?.message ?? "Restore failed", severity: "error" });
                  }
                }}
                onOpenCustomer={(cid) => navigate(`/customer-history/${cid}`)}
              />

              {/* `#log-contact-anchor` is preserved on the wrapper so
                  legacy deep-links that scroll to the action area still
                  resolve. The composer renders Lead Management ·
                  Quick Actions · Follow-up Management as three Papers,
                  visually consistent with the Related Enquiries card
                  above so the whole column reads as one stack. */}
              <Box id="log-contact-anchor">
                <LeadActionsPanel
                  lead={lead}
                  teamUsers={teamUsers}
                  variant="card"
                  tasks={tasks}
                  onLogged={() => loadAll()}
                  onTasksChanged={() => loadAll()}
                  onOpenTask={(t) => setDetailTask(t)}
                  readOnly={!canAct}
                  assigneeLocked={!isAdmin}
                />
              </Box>

              {/* SMS History — per-lead audit of every send attempt
                  (queued / sent / delivered / failed) with provider
                  error reasons + AI-assisted flags. Refreshes on every
                  loadAll() (after a send the Snackbar's onChanged hook
                  triggers it), so the latest row always shows up. */}
              <LeadSmsHistoryCard leadId={lead.id} reloadKey={refreshCounter} />

              {/* Assignment History — the append-only audit trail of
                  who has held this lead and for how long. Visible to
                  every user the lead is visible to (current owner,
                  previous owners, admin). Drives accountability +
                  reassignment auditing per the enterprise spec. */}
              <AssignmentHistoryPanel leadId={lead.id} />
            </Stack>

            {/* ════════════════════════════════════════════════════════
                RIGHT (~35%) — Timeline activity rail (sticky)

                Dedicated vertical history rail that mirrors the side
                activity panels in HubSpot / Salesforce lead workspaces.
                Sticky-pinned at top so the rail stays in view while the
                rep scrolls the long operational column on the left.
                Renders the SAME <LeadActivityTimeline /> component
                used by the drawer's Details-tab preview, the drawer's
                Timeline tab, and the dashboard activity feed — so any
                future change to avatars / role badges / NoteBubbles /
                FieldDiffs / outcome chips / date buckets shows up
                everywhere at once. The 400px column width matches the
                Lead Management / Quick Actions / Follow-up cards on
                the left so the rail reads as a peer surface, not an
                oversized banner.
                Anchor id preserved so legacy deep-links that scroll to
                "#full-timeline-anchor" still resolve. */}
            <Box
              sx={{
                position: { lg: "sticky" },
                top: { lg: 16 },
                alignSelf: "start",
                // Bound height on lg+ so the inner timeline scrolls
                // inside the rail instead of pushing the page down.
                maxHeight: { lg: "calc(100vh - 32px)" },
                overflow: "hidden",
                display: "flex",
              }}
            >
              <Paper id="full-timeline-anchor" elevation={0} sx={{
                p: 2.2,
                borderRadius: SURFACE.cardRadius,
                border: SURFACE.cardBorder,
                bgcolor: SURFACE.card,
                boxShadow: SURFACE.cardShadow,
                transition: "box-shadow 0.2s",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                "&:hover": { boxShadow: SURFACE.cardHover },
              }}>
                <Box mb={1.4} sx={{ flexShrink: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY, letterSpacing: 0.2 }}>
                    Timeline
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.1 }}>
                    Complete lead history and staff actions
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5,
                  "&::-webkit-scrollbar": { width: 6 },
                  "&::-webkit-scrollbar-thumb": { bgcolor: "#dde2e8", borderRadius: 3 },
                }}>
                  <LeadActivityTimeline threadId={lead.id} density="compact" />
                </Box>
              </Paper>
            </Box>
          </Box>


        </Box>
      </Box>

      <Snackbar open={snack.open} autoHideDuration={2000} onClose={() => setSnack((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert severity={snack.severity} variant="filled">{snack.msg}</Alert>
      </Snackbar>

      {lead && (
        <EditLeadDialog
          open={editOpen}
          lead={lead}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            loadAll();
            setSnack({ open: true, msg: "Lead updated", severity: "success" });
          }}
        />
      )}

      {/* ── Centralized Email Compose Modal ──────────────────────────
          Rendered once at the page root. Mounted lazily by MUI Dialog
          so cost is near zero when closed. Opened by every Email
          surface on this page (header rail today, future quick-action
          chips on cards). NEVER a mailto: handoff — the send stays
          inside the CRM so it's threaded, templated, and logged. */}
      {emailModalElement}

      {/* ── Header Add-Note Dialog ─────────────────────────────────
          Distinct from the page-level Quick Actions "Add Note" tile
          so the header rail can launch a quick-capture flow without
          forcing the rep to scroll. Hits addLeadNote → loadAll() so
          the Timeline picks up the new row immediately. ESC + click-
          outside close are MUI Dialog defaults. */}
      <Dialog
        open={headerNoteOpen}
        onClose={() => !headerNoteSaving && setHeaderNoteOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: NAVY, fontSize: 16, pb: 1 }}>
          Add Internal Note
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Visible only to your team. Saved to this lead's timeline.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            maxRows={10}
            value={headerNoteText}
            onChange={(e) => setHeaderNoteText(e.target.value)}
            placeholder="What did you want to capture about this lead?"
            disabled={headerNoteSaving}
            onKeyDown={(e) => {
              /* ⌘/Ctrl+Enter saves — matches the convention used by
                 every other capture surface in the CRM (Slack-style). */
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                handleHeaderNoteSave();
              }
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 14 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setHeaderNoteOpen(false)} disabled={headerNoteSaving}
            sx={{ textTransform: "none", color: "#64748b" }}>
            Cancel
          </Button>
          <Button
            onClick={handleHeaderNoteSave}
            disabled={headerNoteSaving || !headerNoteText.trim()}
            variant="contained"
            disableElevation
            startIcon={headerNoteSaving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <NoteAddIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none", fontWeight: 700, bgcolor: NAVY,
              "&:hover": { bgcolor: "#142b46" },
              "&.Mui-disabled": { bgcolor: "#cbd5e1", color: "#fff" },
            }}
          >
            {headerNoteSaving ? "Saving…" : "Save Note"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Header Schedule-Follow-up Dialog ───────────────────────
          Mirrors the "Schedule Follow-up" tile in QuickActionsCard so
          a follow-up created from the rail and one created from the
          inline panel land in the same Tasks table with identical
          shape. Calls createTask → loadAll() so the activity log +
          follow-up management section both update on success. */}
      <Dialog
        open={headerScheduleOpen}
        onClose={() => !headerSchedSaving && setHeaderScheduleOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: NAVY, fontSize: 16, pb: 1 }}>
          Schedule Follow-up
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Stack gap={1.5}>
            <PremiumDateTimePicker
              label="Follow-up Date & Time *"
              value={headerSchedDate}
              onChange={(v) => setHeaderSchedDate(v)}
            />
            <Box>
              <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: "#94a3b8", letterSpacing: 0.7, textTransform: "uppercase", mb: 0.6 }}>
                Priority
              </Typography>
              <Stack direction="row" gap={0.4}>
                {(["low", "medium", "high", "urgent"] as const).map((p) => {
                  const sel = headerSchedPriority === p;
                  const colors: Record<string, string> = { low: "#94a3b8", medium: "#3b82f6", high: "#f59e0b", urgent: "#ef4444" };
                  return (
                    <Box
                      key={p}
                      role="button"
                      onClick={() => setHeaderSchedPriority(p)}
                      sx={{
                        flex: 1, textAlign: "center", cursor: "pointer", userSelect: "none",
                        py: 0.5, borderRadius: "8px",
                        fontSize: 11.5, fontWeight: sel ? 700 : 600, textTransform: "capitalize",
                        color: sel ? colors[p] : "#94a3b8",
                        border: sel ? `1.5px solid ${colors[p]}` : "1px solid #eef0f4",
                        transition: "all 0.15s",
                        "&:hover": { color: colors[p], borderColor: "#cbd5e1" },
                      }}
                    >
                      {p}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              maxRows={6}
              placeholder="What's this follow-up about?"
              value={headerSchedNotes}
              onChange={(e) => setHeaderSchedNotes(e.target.value)}
              disabled={headerSchedSaving}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setHeaderScheduleOpen(false)} disabled={headerSchedSaving}
            sx={{ textTransform: "none", color: "#64748b" }}>
            Cancel
          </Button>
          <Button
            onClick={handleHeaderScheduleSave}
            disabled={headerSchedSaving || !headerSchedDate}
            variant="contained"
            disableElevation
            startIcon={headerSchedSaving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <ScheduleIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: "none", fontWeight: 700, bgcolor: NAVY,
              "&:hover": { bgcolor: "#142b46" },
              "&.Mui-disabled": { bgcolor: "#cbd5e1", color: "#fff" },
            }}
          >
            {headerSchedSaving ? "Scheduling…" : "Create Follow-up"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
